import { Button } from '@payaca/ambrosia';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { ComponentProps, FunctionComponent, type JSX } from 'react';

import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';
import { TeamListItem } from './useUsersTableFilters';

type IconName = ComponentProps<typeof UntitledIcon>['name'];

type ListEntryProps = {
  icon?: IconName;
  label: string;
  count: number;
  isSelected: boolean;
  onClick: () => void;
};

const ListEntry: FunctionComponent<ListEntryProps> = (props) => {
  const { icon, label, count, isSelected, onClick } = props;

  return (
    <li>
      <button
        type="button"
        aria-current={isSelected ? 'true' : undefined}
        onClick={onClick}
        className={clstx(
          'flex w-full cursor-pointer appearance-none items-center gap-x-2 rounded-lg border border-transparent bg-transparent px-3 py-2 text-left text-base text-gray-800 transition-colors hover:bg-gray-100',
          isSelected && 'bg-gray-100'
        )}
      >
        {icon && (
          <UntitledIcon name={icon} className="size-4 shrink-0 text-gray-500" />
        )}
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <span className="shrink-0 text-gray-500">{count}</span>
      </button>
    </li>
  );
};

type TeamsListProps = {
  allUsersCount: number;
  teams: TeamListItem[];
  selectedTeamId: string | null;
  onSelectTeam: (teamId: string | null) => void;
  onNewTeam: () => void;
  className?: string;
};

const TeamsList: FunctionComponent<TeamsListProps> = (props): JSX.Element => {
  const {
    allUsersCount,
    teams,
    selectedTeamId,
    onSelectTeam,
    onNewTeam,
    className,
  } = props;

  return (
    <nav aria-label="Teams" className={clstx('flex flex-col p-3', className)}>
      <ul className="m-0 flex list-none flex-col p-0">
        <ListEntry
          label="All users"
          count={allUsersCount}
          isSelected={selectedTeamId === null}
          icon="users-02.3"
          onClick={() => onSelectTeam(null)}
        />
      </ul>
      <hr className="my-3 border-gray-200" />
      <h4 className="mb-1 px-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Teams
      </h4>
      <ul className="m-0 mb-5 flex list-none flex-col gap-1 p-0">
        {teams.map((team) => (
          <ListEntry
            key={team.id}
            label={team.name}
            count={team.count}
            isSelected={selectedTeamId === team.id}
            onClick={() => onSelectTeam(team.id)}
          />
        ))}
      </ul>
      <PermissionGuard
        renderIfHasPermissions={[AccountsPermissions.MANAGE_TEAMS]}
      >
        <Button
          variant="secondary"
          size="sm"
          className="w-full"
          onClick={onNewTeam}
        >
          <UntitledIcon name="plus.3" className="h-3.5 w-3.5" />
          Create team
        </Button>
      </PermissionGuard>
    </nav>
  );
};

export default TeamsList;
